import { CompletionArt } from '../ui/controls';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatCompletionTime,
  formatDisplayDate,
  isToday
} from '../../lib/gameUtils';
import { useDialogKeyboardNavigation } from '../../hooks/useDialogKeyboardNavigation';
import {
  getChallengeAdditionalText,
  selectSudokuChallenge,
  type SudokuGameStats
} from './sudokuCompletion';
import type { DailyPuzzleGame } from '../../lib/dailyPuzzle';

interface CountedStatisticProps {
  count: number;
  singularKey: string;
  pluralKey: string;
}

const CountedStatistic = ({ count, singularKey, pluralKey }: CountedStatisticProps) => {
  const { t } = useTranslation();
  if (count === 0) {
    return null;
  }

  return <li>{t(count === 1 ? singularKey : pluralKey, { count })}</li>;
};

interface BooleanStatisticProps {
  isVisible: boolean;
  translationKey: string;
}

const BooleanStatistic = ({ isVisible, translationKey }: BooleanStatisticProps) => {
  const { t } = useTranslation();
  if (!isVisible) {
    return null;
  }

  return <li>{t(translationKey)}</li>;
};

const PerfectSolveStatistic = ({ stats }: { stats: SudokuGameStats }) => {
  const { t } = useTranslation();
  if (stats.errors !== 0 || stats.hintsUsed !== 0) {
    return null;
  }

  return (
    <li className="font-semibold text-green-600 dark:text-green-400">
      {t('perfectSolve')}
    </li>
  );
};

const CompletionStatistics = ({ stats }: { stats: SudokuGameStats }) => {
  return (
    <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300">
      <CountedStatistic
        count={stats.errors}
        singularKey="errorsMade"
        pluralKey="errorsMade_plural"
      />
      <CountedStatistic
        count={stats.pencilErrors}
        singularKey="pencilErrors"
        pluralKey="pencilErrors_plural"
      />
      <CountedStatistic
        count={stats.hintsUsed}
        singularKey="hintsUsed"
        pluralKey="hintsUsed_plural"
      />
      <BooleanStatistic isVisible={stats.coverageUsed} translationKey="coverageUsed" />
      <BooleanStatistic
        isVisible={stats.pencilMarksEntered}
        translationKey="pencilMarksEntered"
      />
      <BooleanStatistic isVisible={stats.autoPencilUsed} translationKey="autoPencilUsed" />
      <PerfectSolveStatistic stats={stats} />
    </ul>
  );
};

const CompletedDayMessage = ({ isVisible }: { isVisible: boolean }) => {
  const { t } = useTranslation();
  if (!isVisible) {
    return null;
  }

  return (
    <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border-2 border-yellow-300 dark:border-yellow-700">
      <p className="text-yellow-700 dark:text-yellow-300 font-bold text-center text-lg mb-2">
        {t('dayCompleted')} ⭐
      </p>
      <p className="text-yellow-600 dark:text-yellow-400 text-center">
        {t('completedAllGames')}
      </p>
    </div>
  );
};

interface ChallengeMessageProps {
  stats: SudokuGameStats;
  elapsedTime: number;
  difficulty: number;
}

const ChallengeMessage = ({ stats, elapsedTime, difficulty }: ChallengeMessageProps) => {
  const { t } = useTranslation();
  const challenge = selectSudokuChallenge(stats, elapsedTime);
  const additional = getChallengeAdditionalText(challenge, difficulty, t);

  return (
    <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
      <p className="text-blue-700 dark:text-blue-300 font-medium">
        {t(challenge.key, { additional })}
      </p>
    </div>
  );
};

interface TryAgainButtonProps {
  stats: SudokuGameStats;
  elapsedTime: number;
  onTryAgain: () => void;
}

const TryAgainButton = ({ stats, elapsedTime, onTryAgain }: TryAgainButtonProps) => {
  const { t } = useTranslation();
  if (stats.errors === 0 && stats.hintsUsed === 0 && elapsedTime <= 300) {
    return null;
  }

  return (
    <button
      onClick={onTryAgain}
      className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      {t('tryAgain')}
    </button>
  );
};

const TryTodaysSudokuButton = ({
  puzzleDate,
  onTryToday
}: {
  puzzleDate: Date;
  onTryToday: () => void;
}) => {
  const { t } = useTranslation();
  if (isToday(puzzleDate)) {
    return null;
  }

  return (
    <button
      onClick={onTryToday}
      className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt text-white rounded hover:bg-cobalt focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      {t('tryTodaysSudoku')}
    </button>
  );
};

interface TryOtherGameButtonProps {
  gameType: Exclude<DailyPuzzleGame, 'sudoku'>;
  incompleteGames: DailyPuzzleGame[];
  puzzleDate: Date;
  onTryGame: () => void;
}

const TryOtherGameButton = ({
  gameType,
  incompleteGames,
  puzzleDate,
  onTryGame
}: TryOtherGameButtonProps) => {
  const { t, i18n } = useTranslation();
  if (!incompleteGames.includes(gameType)) {
    return null;
  }

  const todayKey = gameType === '15puzzle' ? 'tryTodays15Puzzle' : 'tryTodays2048';
  const historicalKey = gameType === '15puzzle' ? 'try15PuzzleForDate' : 'try2048ForDate';
  const label = isToday(puzzleDate)
    ? t(todayKey)
    : t(historicalKey, {
      date: new Intl.DateTimeFormat(i18n.language || 'en', {
        month: 'long',
        day: 'numeric'
      }).format(puzzleDate)
    });

  return (
    <button
      onClick={onTryGame}
      className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-lagoon text-white rounded hover:bg-teal-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      {label}
    </button>
  );
};

interface SudokuCompletionDialogProps {
  isOpen: boolean;
  puzzleDate: Date;
  elapsedTime: number;
  stats: SudokuGameStats;
  difficulty: number;
  allGamesCompleted: boolean;
  incompleteGames: DailyPuzzleGame[];
  onClose: () => void;
  onTryAgain: () => void;
  onTryToday: () => void;
  onTryFifteenPuzzle: () => void;
  onTry2048: () => void;
}

export const SudokuCompletionDialog = ({
  isOpen,
  puzzleDate,
  elapsedTime,
  stats,
  difficulty,
  allGamesCompleted,
  incompleteGames,
  onClose,
  onTryAgain,
  onTryToday,
  onTryFifteenPuzzle,
  onTry2048
}: SudokuCompletionDialogProps) => {
  const { t, i18n } = useTranslation();
  const handleEscape = useCallback(() => onClose(), [onClose]);
  const dialogRef = useDialogKeyboardNavigation(isOpen, handleEscape);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={dialogRef}
      id="sudoku-completion-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sudoku-completion-title"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 max-w-md w-full mx-4 max-h-[90dvh] overflow-y-auto">
        <CompletionArt />
        <h2
          id="sudoku-completion-title"
          className="text-base font-bold text-gray-900 dark:text-white mb-4 break-words text-center"
        >
          {formatDisplayDate(puzzleDate, t, i18n, 'sudoku', true)}
        </h2>

        <div className="space-y-3 mb-6">
          <p className="text-gray-700 dark:text-gray-300">
            {t('time')}: {formatCompletionTime(elapsedTime)}
          </p>

          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            {t('gameStatistics')}
          </h3>
          <CompletionStatistics stats={stats} />
          <CompletedDayMessage isVisible={allGamesCompleted} />
          <ChallengeMessage stats={stats} elapsedTime={elapsedTime} difficulty={difficulty} />
        </div>

        <div className="flex flex-col gap-3">
          <TryAgainButton stats={stats} elapsedTime={elapsedTime} onTryAgain={onTryAgain} />
          <TryTodaysSudokuButton puzzleDate={puzzleDate} onTryToday={onTryToday} />
          <TryOtherGameButton
            gameType="15puzzle"
            incompleteGames={incompleteGames}
            puzzleDate={puzzleDate}
            onTryGame={onTryFifteenPuzzle}
          />
          <TryOtherGameButton
            gameType="2048"
            incompleteGames={incompleteGames}
            puzzleDate={puzzleDate}
            onTryGame={onTry2048}
          />
          <button
            onClick={onClose}
            className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
};
